/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * useDivisionRoster Hook
 *
 * Manages division roster (user assignments) data and operations
 */

import { useAuthSafe } from "@/hooks/auth/use-auth-safe";
import { useOrganizationQueryGuard } from "@/hooks/use-organization-query";
import { createApiClient, isApiFailure } from "@/lib/api/api-client";
import { toastFromApiFailure } from "@/lib/api/toast-utils";
import { QUERY_GC_TIME, QUERY_STALE_TIME } from "@/lib/constants/query-config";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useMemo } from "react";

// ============================================================================
// TYPES
// ============================================================================

export type DivisionRole =
  | "executive"
  | "lead"
  | "member"
  | "support"
  | "viewer";

export interface RosterMember {
  id: string;
  user_id: string;
  division_id: string;
  role_in_division: DivisionRole | null;
  is_primary: boolean | null;
  assigned_at: string;
  assigned_by: string | null;
  user: {
    user_id: string;
    email: string;
    first_name: string | null;
    last_name: string | null;
    role: string;
  } | null;
}

export interface RosterState {
  roster: RosterMember[];
  loading: boolean;
  error: string | null;
}

// ============================================================================
// HOOK
// ============================================================================

export function useDivisionRoster(divisionId: string | null) {
  const queryClient = useQueryClient();
  const { getToken } = useAuthSafe();
  const { organizationId, queryEnabled, isOrganizationReady } =
    useOrganizationQueryGuard({
      enabled: !!divisionId,
      debugKey: "useDivisionRoster",
    });

  const api = useMemo(
    () =>
      createApiClient({
        getToken,
        getOrganizationId: () => organizationId ?? undefined,
        autoRetry429: true,
      }),
    [getToken, organizationId],
  );

  // ============================================================================
  // FETCH ROSTER
  // ============================================================================

  const rosterQuery = useQuery<{ roster: RosterMember[] }>({
    queryKey: ["division-roster", organizationId, divisionId],
    enabled: queryEnabled,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: QUERY_STALE_TIME.STANDARD,
    gcTime: QUERY_GC_TIME.STANDARD,
    meta: { entity: "division-roster" },
    queryFn: async () => {
      if (!organizationId || !isOrganizationReady) {
        throw new Error(
          "Still loading your organization. Give it a second and try again.",
        );
      }

      const res = await api.get<{ roster?: RosterMember[] } | RosterMember[]>(
        `/api/divisions/${divisionId}/roster`,
      );

      if (isApiFailure(res)) {
        throw new Error(res.message);
      }

      const rawData = res.data ?? {};
      const roster = Array.isArray(rawData)
        ? rawData
        : ((rawData as { roster?: RosterMember[] })?.roster ?? []);

      return { roster };
    },
  });

  const rosterState = useMemo<RosterState>(
    () => ({
      roster: rosterQuery.data?.roster ?? [],
      loading: rosterQuery.isLoading || rosterQuery.isFetching,
      error: rosterQuery.error?.message ?? null,
    }),
    [
      rosterQuery.data,
      rosterQuery.isLoading,
      rosterQuery.isFetching,
      rosterQuery.error,
    ],
  );

  // ============================================================================
  // ADD MEMBER
  // ============================================================================

  const addMemberMutation = useMutation({
    mutationFn: async ({
      userId,
      role = "member",
      isPrimary = false,
    }: {
      userId: string;
      role?: DivisionRole;
      isPrimary?: boolean;
    }) => {
      if (!divisionId || !organizationId || !isOrganizationReady) {
        throw new Error(
          "Still loading your organization. Give it a second and try again.",
        );
      }

      const res = await api.post<unknown>(
        `/api/divisions/${divisionId}/roster`,
        { user_id: userId, role, is_primary: isPrimary },
      );

      if (isApiFailure(res)) {
        toastFromApiFailure("Failed to add member", res);
        throw new Error(res.message);
      }

      return res.data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["division-roster", organizationId, divisionId],
      });
    },
  });

  // ============================================================================
  // UPDATE MEMBER
  // ============================================================================

  const updateMemberMutation = useMutation({
    mutationFn: async ({
      userId,
      role,
      isPrimary,
    }: {
      userId: string;
      role?: DivisionRole;
      isPrimary?: boolean;
    }) => {
      if (!divisionId || !organizationId || !isOrganizationReady) {
        throw new Error(
          "Still loading your organization. Give it a second and try again.",
        );
      }

      const res = await api.patch<unknown>(
        `/api/divisions/${divisionId}/roster/${userId}`,
        { role, is_primary: isPrimary },
      );

      if (isApiFailure(res)) {
        toastFromApiFailure("Failed to update member", res);
        throw new Error(res.message);
      }

      return res.data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["division-roster", organizationId, divisionId],
      });
    },
  });

  // ============================================================================
  // REMOVE MEMBER
  // ============================================================================

  const removeMemberMutation = useMutation({
    mutationFn: async (userId: string) => {
      if (!divisionId) {
        throw new Error("Division ID is required");
      }

      const res = await api.delete<unknown>(
        `/api/divisions/${divisionId}/roster/${userId}`,
      );

      if (isApiFailure(res)) {
        toastFromApiFailure("Failed to remove member", res);
        throw new Error(res.message);
      }

      return res.data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["division-roster", organizationId, divisionId],
      });
    },
  });

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    // State
    ...rosterState,
    isLoading: rosterState.loading,
    hasError: !!rosterState.error,

    // Actions
    addMember: addMemberMutation.mutateAsync,
    updateMember: updateMemberMutation.mutateAsync,
    removeMember: removeMemberMutation.mutateAsync,
    refetch: rosterQuery.refetch,

    // Mutations (for direct access if needed)
    addMemberMutation,
    updateMemberMutation,
    removeMemberMutation,
  };
}
